import { useCallback, useEffect, useRef } from 'react';
import extraGetEvent from '@@app/providers/analytics/extraGetEvent.js';
import extraFormatPayload from '@@app/providers/analytics/extraFormatPayload.js';
import { isEmpty, propOr } from 'ramda';
import { useProcess } from '@fasstech/oip-starter-utils/app';

const useEvents = ({
  loaded,
  provider,
  getEvent,
  loading,
  getCommonData,
  analyticsEvents,
  getAnalyticsInformation
}) => {
  const { context } = useProcess();
  const hasTrackedRef = useRef(false);

  const trackEvent = useCallback(
    (event, payload = {}) => {
      if (!loaded) {
        return;
      }

      const clientEvent = extraGetEvent(event);
      const formattedPayload = extraFormatPayload(payload);

      console.debug(
        `[${provider}] triggering ${clientEvent} with data ${JSON.stringify(formattedPayload)}`
      );

      switch (provider) {
        case 'gtm':
          window.dataLayer?.push({
            event: 'reset',
            selectedFormulas: undefined,
            monthlyPrices: undefined,
            yearlyPrices: undefined,
            signatureType: undefined
          });

          window.dataLayer?.push(formattedPayload);
          break;
        case 'tagcommander':
          tC.event[clientEvent](this, formattedPayload);
          break;
        default:
      }
    },
    [provider, loaded]
  );

  useEffect(() => {
    if (!loaded) return;

    const pageViewEvent = getEvent('PAGE_VIEW');
    if (!pageViewEvent && loading) return;

    const triggerPageView = () => {
      if (hasTrackedRef.current === context.viewId) return;

      trackEvent('page_view', {
        event: propOr('', 'eventName', pageViewEvent),
        actionCode: propOr('', 'eventCode', pageViewEvent),
        ...getCommonData()
      });

      hasTrackedRef.current = context.viewId;
    };

    if (provider === 'tagcommander') {
      const waitForTC = () => {
        if (window.tC?.event?.page_view) {
          triggerPageView();
        } else {
          requestAnimationFrame(waitForTC);
        }
      };
      waitForTC();
    } else {
      triggerPageView();
    }
  }, [context.viewId, getAnalyticsInformation, trackEvent, loading, loaded, provider]);

  const trackButtonClick = useCallback(
    ({ label, code, event, action }) => {
      if (isEmpty(analyticsEvents)) {
        return;
      }

      const buttonClickEvent = getEvent('BUTTON_CLICK');

      if (buttonClickEvent) {
        trackEvent('button_click', {
          event: event || propOr('', 'eventName', buttonClickEvent),
          actionCode: action || propOr('', 'eventCode', buttonClickEvent),
          buttonLabel: label,
          buttonCode: code,
          ...getCommonData()
        });
      }
    },
    [getCommonData]
  );

  return {
    trackEvent,
    trackButtonClick
  };
};

export default useEvents;
